// src/components/Navbar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import * as Dialog from '@radix-ui/react-dialog';
import { Menu, X } from 'lucide-react';
import logo from '../../assets/logos/logo.JPG';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Board', path: '/board' },
    { name: 'Events' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'News' },
    { name: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`bg-white/90 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-lg' : 'shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12">
        <div className="flex justify-between items-center h-16 lg:h-20">

          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0"
          >
            <Link to="/">
              <img src={logo} alt="Logo" className="w-16 h-16" />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <NavigationMenu.Root className="relative">
              <NavigationMenu.List className="flex space-x-2 xl:space-x-4 2xl:space-x-8">
                {navItems.map((item) => (
                  <NavigationMenu.Item key={item.name}>
                    <NavigationMenu.Link asChild>
                      <Link
                        to={item.path}
                        className={`px-4 py-2 rounded-lg text-sm xl:text-base font-medium transition-all duration-200 ${
                          location.pathname === item.path
                            ? 'text-blue-600 bg-blue-50 shadow-sm'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100'
                        }`}
                      >
                        {item.name}
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>
                ))}
              </NavigationMenu.List>
            </NavigationMenu.Root>
          </div>

          {/* Donate Button With Dialog */}
          <div className="hidden lg:block">
            <Dialog.Root>
              <Dialog.Trigger asChild>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary"
                >
                  Donate
                </motion.button>
              </Dialog.Trigger>

              <Dialog.Portal>
                {/* Backdrop */}
                <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />

                {/* Modal Content */}
                <Dialog.Content
                  className="fixed top-1/2 left-1/2 w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 
                  bg-white p-6 rounded-2xl shadow-xl z-[60] animate-in fade-in duration-200"
                >
                  <Dialog.Title className="text-xl font-semibold text-gray-800 mb-2">
                    Support Our Cause
                  </Dialog.Title>

                  <p className="text-gray-700 leading-relaxed mb-4">
                    You are kindly requested to support our great cause by donating 
                    through any of the MOMO numbers provided below. Be a part of 
                    this great mission. Thank you.
                  </p>

                  <div className="space-y-3">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-semibold text-gray-900">1. MOMO Account:</p>
                      <p className="text-blue-600 font-bold text-lg">0551605410</p>
                      <p className="text-gray-600 text-sm">Acc Name: ----------</p>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-semibold text-gray-900">2. MOMO Account:</p>
                      <p className="text-blue-600 font-bold text-lg">0202683143</p>
                      <p className="text-gray-600 text-sm">Acc Name: ----------</p>
                    </div>
                  </div>

                  <Dialog.Close asChild>
                    <button className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium">
                      Close
                    </button>
                  </Dialog.Close>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </div>

          {/* Mobile Hamburger */}
          <div className="lg:hidden">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-gray-100"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-200"
            >
              <div className="px-2 pt-2 pb-4 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname === item.path
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}

                {/* Mobile Donate Button */}
                <Dialog.Root>
                  <Dialog.Trigger asChild>
                    <button className="w-full mt-2 btn-primary">Donate</button>
                  </Dialog.Trigger>

                  <Dialog.Portal>
                    <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
                    <Dialog.Content
                      className="fixed top-1/2 left-1/2 w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 
                      bg-white p-6 rounded-2xl shadow-xl z-[60]"
                    >
                      <Dialog.Title className="text-xl font-semibold text-gray-800 mb-2">
                        Support Our Cause
                      </Dialog.Title>

                      <p className="text-gray-700 leading-relaxed mb-4">
                        You are kindly requested to support our great cause by donating 
                        through any of the MOMO numbers provided below. Thank you.
                      </p>

                      <div className="space-y-3">
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="font-semibold text-gray-900">1. MOMO Account:</p>
                          <p className="text-blue-600 font-bold text-lg">0277393</p>
                          <p className="text-gray-600 text-sm">Acc Name: ----------</p>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="font-semibold text-gray-900">2. MOMO Account:</p>
                          <p className="text-blue-600 font-bold text-lg">02883143</p>
                          <p className="text-gray-600 text-sm">Acc Name: ----------</p>
                        </div>
                      </div>

                      <Dialog.Close asChild>
                        <button className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium">
                          Close
                        </button>
                      </Dialog.Close>
                    </Dialog.Content>
                  </Dialog.Portal>
                </Dialog.Root>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
